import { useAppDispatch, useAppSelector } from "app/hooks"
import {
  getWeeklyOverview,
  getWeeklyWater,
  resetWeeklyData,
} from "./overviewSlice"
import { useEffect, useRef } from "react"
import { useSearchParams } from "react-router-dom"
import dayjs from "dayjs"
import { DATE_PARAM_FORMAT } from "config"
import { getTimeToRefreshOnNextDay } from "utils/dateTime"

export const useDateSearchParam = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const dateString =
    searchParams.get("date") ?? dayjs().format(DATE_PARAM_FORMAT)
  const date = dayjs(dateString).startOf("day")

  const isToday = date.isSame(dayjs().startOf("day"))

  const onBackToYesterday = () => {
    setSearchParams({ date: date.subtract(1, "day").format(DATE_PARAM_FORMAT) })
  }

  const onGoToTomorrow = () => {
    setSearchParams({ date: date.add(1, "day").format(DATE_PARAM_FORMAT) })
  }

  const onDateChange = (date: dayjs.Dayjs) => {
    setSearchParams({ date: date.format(DATE_PARAM_FORMAT) })
  }

  const onGoToNextWeek = () => {
    setSearchParams({ date: date.add(1, "week").format(DATE_PARAM_FORMAT) })
  }

  const onGoToPreviousWeek = () => {
    setSearchParams({
      date: date.subtract(1, "week").format(DATE_PARAM_FORMAT),
    })
  }

  return {
    isToday,
    date,
    dateString,
    onBackToYesterday,
    onGoToTomorrow,
    onDateChange,
    onGoToNextWeek,
    onGoToPreviousWeek,
  }
}

export const useWeeklyWater = (dateString: string) => {
  const dispatch = useAppDispatch()
  const weeklyWater = useAppSelector((state) => state.overview.weeklyWater)
  const getWeeklyWaterLoading = useAppSelector(
    (state) => state.overview.getWeeklyWaterLoading,
  )

  const start = dayjs(dateString).startOf("week")
  const end = start.endOf("week")
  const weekStartString = start.format(DATE_PARAM_FORMAT)
  const refreshOnNextDateTimeoutRef = useRef<any>(null)

  useEffect(() => {
    const fetchWeeklyWater = () => {
      dispatch(
        getWeeklyWater({
          start: start.format(DATE_PARAM_FORMAT),
          end: end.format(DATE_PARAM_FORMAT),
        }),
      )
    }

    fetchWeeklyWater()

    const timeToRefresh = getTimeToRefreshOnNextDay()

    refreshOnNextDateTimeoutRef.current = setTimeout(() => {
      fetchWeeklyWater()
    }, timeToRefresh)

    return () => {
      clearTimeout(refreshOnNextDateTimeoutRef.current)
    }
  }, [weekStartString])

  return {
    weeklyWater,
    getWeeklyWaterLoading,
  }
}

export const useResetWeeklyDataOnUnmount = () => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    return () => {
      dispatch(resetWeeklyData())
    }
  }, [])
}

export const useWeeklyOverview = (dateString: string) => {
  const dispatch = useAppDispatch()
  const weeklyOverview = useAppSelector(
    (state) => state.overview.weeklyOverview,
  )
  const getWeeklyOverviewLoading = useAppSelector(
    (state) => state.overview.getWeeklyOverviewLoading,
  )
  const getWeeklyOverviewFailed = useAppSelector(
    (state) => state.overview.getWeeklyOverviewFailed,
  )

  const start = dayjs(dateString).startOf("week")
  const end = start.endOf("week")
  const weekStartString = start.format(DATE_PARAM_FORMAT)
  const refreshOnNextDateTimeoutRef = useRef<any>(null)

  useEffect(() => {
    const fetchWeeklyOverview = () => {
      dispatch(
        getWeeklyOverview({
          start: start.format(DATE_PARAM_FORMAT),
          end: end.format(DATE_PARAM_FORMAT),
        }),
      )
    }

    fetchWeeklyOverview()

    const timeToRefresh = getTimeToRefreshOnNextDay()

    refreshOnNextDateTimeoutRef.current = setTimeout(() => {
      fetchWeeklyOverview()
    }, timeToRefresh)

    return () => {
      clearTimeout(refreshOnNextDateTimeoutRef.current)
    }
  }, [weekStartString])

  return {
    weeklyOverview,
    getWeeklyOverviewLoading,
    getWeeklyOverviewFailed,
  }
}
